import { useState } from 'react';
import { Layers, Plus, Settings2, Warehouse } from 'lucide-react';
import type { StoreroomDto } from '@eombor/contracts';
import { Badge, IconButton, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { useMyStorerooms } from '../model/subdivision.queries.js';
import { StoreroomDialog } from './StoreroomDialog.js';

export interface StoreroomStripProps {
  /** `null` — hammasi. */
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

/**
 * OMBORLAR TASMASI — qoldiq ustida: filtr ham, boshqaruv ham.
 *
 * Har plitka o'z omborining «pasporti»: nomi, maqsadi, nechta mahsulot va
 * nechta zona. Sozlash tugmasi plitkaning ICHIDA emas, yonida turadi:
 * tugma ichida tugma bo'lolmaydi, va plitkani bosish — filtr, tishli
 * g'ildirak — sozlash, bular ikki alohida niyat.
 *
 * Telefonda tasma yon tomonga suriladi (snap bilan), sahifa esa suriladi emas.
 */
export const StoreroomStrip = ({ selectedId, onSelect }: StoreroomStripProps) => {
  const rooms = useMyStorerooms();
  const { canAnywhere } = usePermission();
  const [dialog, setDialog] = useState<{ id: string | null } | null>(null);

  if (rooms.isPending) return <Skeleton className="h-[5.5rem] w-full" />;
  if (rooms.isError) return null;

  const list = rooms.data;
  const total = list.reduce((sum, room) => sum + room.lotCount, 0);
  const editing = dialog?.id == null ? null : (list.find((room) => room.id === dialog.id) ?? null);

  return (
    <>
      <div
        role="group"
        aria-label={t('storeroom.stripLabel')}
        className="-mx-1 flex snap-x gap-2.5 overflow-x-auto px-1 pt-0.5 pb-2"
      >
        <Tile
          selected={selectedId === null}
          onClick={() => onSelect(null)}
          icon={<Layers size={18} />}
          name={t('storeroom.all')}
          meta={t('storeroom.lots', { count: total })}
        />

        {list.map((room) => (
          <Tile
            key={room.id}
            selected={selectedId === room.id}
            onClick={() => onSelect(room.id)}
            icon={<Warehouse size={18} />}
            name={room.name}
            meta={room.purpose ?? t('storeroom.lots', { count: room.lotCount })}
            archived={!room.isActive}
            footer={<RoomFooter room={room} />}
            action={
              <IconButton
                icon={<Settings2 size={15} />}
                label={t('storeroom.settings', { name: room.name })}
                onClick={() => setDialog({ id: room.id })}
              />
            }
          />
        ))}

        {canAnywhere('storeroom.manage') ? (
          <div className="snap-start">
            <button
              type="button"
              onClick={() => setDialog({ id: null })}
              className={cn(
                'flex h-full min-h-[5.25rem] w-40 flex-col items-center justify-center gap-1.5',
                'rounded-[var(--radius-lg)] border border-dashed border-border-strong text-sm font-medium text-text-muted',
                'transition-colors duration-[var(--motion-fast)] hover:border-accent hover:bg-accent-soft hover:text-accent',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
              )}
            >
              <Plus size={18} aria-hidden />
              {t('storeroom.add')}
            </button>
          </div>
        ) : null}
      </div>

      {dialog === null ? null : (
        <StoreroomDialog
          key={dialog.id ?? 'new'}
          open
          storeroom={editing}
          onClose={() => setDialog(null)}
          onCreated={(created) => onSelect(created.id)}
        />
      )}
    </>
  );
};

const RoomFooter = ({ room }: { room: StoreroomDto }) => (
  <span className="flex flex-wrap items-center gap-1.5">
    <Badge tone={room.lotCount > 0 ? 'accent' : 'neutral'}>{t('storeroom.lots', { count: room.lotCount })}</Badge>
    {room.zones.length === 0 ? null : (
      <Badge tone="neutral">{t('storeroom.zonesCount', { count: room.zones.length })}</Badge>
    )}
    {room.isActive ? null : <Badge tone="warn">{t('storeroom.archived')}</Badge>}
  </span>
);

const Tile = ({
  selected,
  onClick,
  icon,
  name,
  meta,
  footer,
  action,
  archived = false,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  name: string;
  meta: string;
  footer?: React.ReactNode;
  action?: React.ReactNode;
  archived?: boolean;
}) => (
  <div
    className={cn(
      'relative flex w-60 shrink-0 snap-start flex-col rounded-[var(--radius-lg)] border',
      'transition-[border-color,background-color,box-shadow] duration-[var(--motion-fast)]',
      selected
        ? 'border-accent bg-accent-soft shadow-[0_0_0_1px_var(--color-accent)]'
        : 'border-border bg-surface shadow-[var(--shadow-1)] hover:border-border-strong',
      archived ? 'opacity-60' : undefined,
    )}
  >
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'flex flex-1 flex-col gap-2 rounded-[var(--radius-lg)] p-3 pr-11 text-left',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
      )}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <span
          aria-hidden
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-md)]',
            selected ? 'bg-accent text-[var(--color-accent-contrast)]' : 'bg-surface-2 text-text-muted',
          )}
        >
          {icon}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-text">{name}</span>
          <span className="truncate text-xs text-text-muted">{meta}</span>
        </span>
      </span>
      {footer}
    </button>
    {action === undefined ? null : <span className="absolute top-2 right-2">{action}</span>}
  </div>
);
